<template>
  <div class="container-fluid">
    <div class="page-title">
      <div class="row">
        <div class="col-6">
          <h3>Detalle de cliente</h3>
        </div>
        <div class="col-6 text-end">
          <button type="button" class="btn btn-outline-secondary" @click="$router.push({ name: 'consultar-clientes' })">
            Regresar
          </button>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <h4>Información del cliente</h4>
          </div>
          <div class="card-body">
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status"><span class="visually-hidden">Cargando...</span></div>
            </div>
            <div v-else-if="errorMsg" class="alert alert-danger">{{ errorMsg }}</div>
            <div v-else-if="client">
              <div class="row g-3">
                <div class="col-md-6"><strong>Nombre:</strong> {{ client.nombre || '-' }}</div>
                <div class="col-md-6"><strong>Email:</strong> {{ client.email || '-' }}</div>
                <div class="col-md-6"><strong>Teléfono:</strong> {{ client.phone || '-' }}</div>
                <div class="col-md-6"><strong>Rol:</strong> {{ roleLabel(client.role) }}</div>
                <div class="col-md-6"><strong>Estado:</strong> {{ client.activo ? 'Activo' : 'Inactivo' }}</div>
                <div class="col-md-6"><strong>Registro:</strong> {{ formatDate(client.createdAt) }}</div>
                <div class="col-md-6"><strong>Verificación email:</strong> {{ verificationLabel(client.status) }}</div>
                <div class="col-md-6"><strong>Verificación teléfono:</strong> {{ client.phoneVerified ? 'Verificado' : 'Pendiente' }}</div>
                <div class="col-md-6"><strong>Documento de identidad:</strong> {{ client.identityDocumentType || '-' }}</div>
                <div class="col-md-6">
                  <strong>DNI frente:</strong>
                  <a v-if="client.dniFrontUrl" :href="client.dniFrontUrl" target="_blank" rel="noopener">Ver documento</a>
                  <span v-else>-</span>
                </div>
                <div class="col-md-6">
                  <strong>DNI reverso:</strong>
                  <a v-if="client.dniBackUrl" :href="client.dniBackUrl" target="_blank" rel="noopener">Ver documento</a>
                  <span v-else>-</span>
                </div>
                <div class="col-md-6"><strong>RFC:</strong> {{ client.rfc || '-' }}</div>
                <div class="col-md-6"><strong>Razón social:</strong> {{ client.razonSocial || '-' }}</div>
                <div class="col-md-6"><strong>Dirección fiscal:</strong> {{ client.direccionFiscal || '-' }}</div>
                <div class="col-md-6"><strong>Código postal fiscal:</strong> {{ client.codigoPostalFiscal || '-' }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <h4>Stripe del cliente</h4>
          </div>
          <div class="card-body">
            <div v-if="stripeLoading" class="text-center py-3">
              <div class="spinner-border text-primary" role="status"><span class="visually-hidden">Cargando...</span></div>
            </div>
            <template v-else>
              <div class="row g-3">
                <div class="col-md-3">
                  <div class="p-3 border rounded h-100">
                    <div class="small text-muted">Cuenta conectada</div>
                    <div class="fw-semibold">{{ stripeStatus.connected ? 'Sí' : 'No' }}</div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="p-3 border rounded h-100">
                    <div class="small text-muted">Onboarding</div>
                    <div class="fw-semibold">{{ stripeStatus.onboardingCompleted ? 'Completado' : 'Pendiente' }}</div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="p-3 border rounded h-100">
                    <div class="small text-muted">Cobros habilitados</div>
                    <div class="fw-semibold">{{ stripeStatus.chargesEnabled ? 'Sí' : 'No' }}</div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="p-3 border rounded h-100">
                    <div class="small text-muted">Payouts habilitados</div>
                    <div class="fw-semibold">{{ stripeStatus.payoutsEnabled ? 'Sí' : 'No' }}</div>
                  </div>
                </div>
              </div>

              <div class="mt-3 small text-muted">
                <div><strong>Account ID:</strong> {{ stripeStatus.stripeAccountId || 'Aún no generado' }}</div>
              </div>

              <div class="d-flex gap-2 mt-4 flex-wrap">
                <button type="button" class="btn btn-primary" :disabled="stripeActionLoading" @click="startStripeOnboarding">
                  {{ stripeActionLoading ? 'Procesando...' : (stripeStatus.connected ? 'Continuar onboarding' : 'Conectar con Stripe') }}
                </button>
                <button
                  type="button"
                  class="btn btn-outline-primary"
                  :disabled="stripeActionLoading || !stripeStatus.connected"
                  @click="openStripeDashboard"
                >
                  Abrir dashboard Stripe
                </button>
                <button type="button" class="btn btn-outline-secondary" :disabled="stripeActionLoading" @click="loadStripeStatus">
                  Refrescar estado
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import { clientsApi } from '@/api/clients';

export default {
  name: 'DetalleCliente',
  data() {
    return {
      loading: false,
      stripeLoading: false,
      stripeActionLoading: false,
      errorMsg: '',
      client: null,
      stripeStatus: {
        stripeAccountId: null,
        connected: false,
        onboardingCompleted: false,
        chargesEnabled: false,
        payoutsEnabled: false,
      },
    };
  },
  mounted() {
    this.loadClient();
    this.loadStripeStatus();
  },
  methods: {
    roleLabel(role) {
      if (role === 'CONDUCTOR_VIAJERO') return 'Viajero';
      if (role === 'CLIENTE_REMITENTE') return 'Usuario';
      return role || '-';
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
    verificationLabel(status) {
      if (status === 'ACTIVO') return 'Verificado';
      if (status === 'PENDIENTE') return 'Pendiente';
      return status || '-';
    },
    async loadClient() {
      const id = this.$route.params.id;
      this.loading = true;
      this.errorMsg = '';
      try {
        const { data } = await clientsApi.getById(id);
        this.client = data;
      } catch (e) {
        this.errorMsg = e.response?.data?.message || 'No se pudo cargar el cliente.';
      } finally {
        this.loading = false;
      }
    },
    async loadStripeStatus() {
      const id = this.$route.params.id;
      this.stripeLoading = true;
      try {
        const { data } = await clientsApi.getStripeStatus(id);
        this.stripeStatus = {
          stripeAccountId: data?.stripeAccountId || null,
          connected: Boolean(data?.connected),
          onboardingCompleted: Boolean(data?.onboardingCompleted),
          chargesEnabled: Boolean(data?.chargesEnabled),
          payoutsEnabled: Boolean(data?.payoutsEnabled),
        };
      } catch (e) {
        const msg = e.response?.data?.message || 'No se pudo consultar Stripe del cliente.';
        await Swal.fire({
          icon: 'error',
          title: 'Stripe',
          text: Array.isArray(msg) ? msg.join(' ') : msg,
          confirmButtonColor: '#0d6efd',
        });
      } finally {
        this.stripeLoading = false;
      }
    },
    async startStripeOnboarding() {
      const id = this.$route.params.id;
      this.stripeActionLoading = true;
      try {
        const { data } = await clientsApi.createStripeOnboardLink(id);
        if (!data?.url) throw new Error('Stripe no devolvió URL de onboarding.');
        window.location.href = data.url;
      } catch (e) {
        const msg = e.response?.data?.message || e.message || 'No se pudo iniciar onboarding de Stripe.';
        await Swal.fire({
          icon: 'error',
          title: 'Stripe',
          text: Array.isArray(msg) ? msg.join(' ') : msg,
          confirmButtonColor: '#0d6efd',
        });
      } finally {
        this.stripeActionLoading = false;
      }
    },
    async openStripeDashboard() {
      const id = this.$route.params.id;
      this.stripeActionLoading = true;
      try {
        const { data } = await clientsApi.createStripeDashboardLink(id);
        if (!data?.url) throw new Error('Stripe no devolvió URL de dashboard.');
        window.open(data.url, '_blank', 'noopener');
      } catch (e) {
        const msg = e.response?.data?.message || e.message || 'No se pudo abrir dashboard de Stripe.';
        await Swal.fire({
          icon: 'error',
          title: 'Stripe',
          text: Array.isArray(msg) ? msg.join(' ') : msg,
          confirmButtonColor: '#0d6efd',
        });
      } finally {
        this.stripeActionLoading = false;
      }
    },
  },
};
</script>

